import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    fetchInspectionTemplates, fetchInspectionSchedules, fetchInspection, fetchInspectionSummary,
    InspectionTemplate, InspectionSchedule, InspectionDetail, InspectionSummary,
} from '../services/inspectionsApi';
import ActiveInspection from './inspections/ActiveInspection';
import RoundPanel from './inspections/RoundPanel';
import PlannedPanel, { PlannedInfo } from './inspections/PlannedPanel';
import TodoTab from './inspections/TodoTab';
import HistoryTab from './inspections/HistoryTab';
import SchedulesTab from './inspections/SchedulesTab';
import TemplatesTab from './inspections/TemplatesTab';
import YearTab from './inspections/YearTab';
import PageHeader from './shared/PageHeader';
import { ui } from '../styles/shared';
import { fmtDate } from '../utils/format';

type Tab = 'todo' | 'year' | 'history' | 'schedules' | 'templates';

/**
 * Egenkontroll-sidan: sidhuvud, felrad, öppen kontroll, gruppfilter och flikar.
 * Varje flik ligger i en egen fil under ./inspections och laddar sina egna listor.
 * Mallar, scheman och räknarna delas av flera flikar och laddas här.
 */
const Inspections: React.FC = () => {
    const { isAdmin, isSuperuser } = useAuth();
    const canEdit = isAdmin || isSuperuser;

    const [tab, setTab] = useState<Tab>('todo');
    const [group, setGroup] = useState<string>('');   // '' = alla grupper
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<InspectionSummary | null>(null);
    const [templates, setTemplates] = useState<InspectionTemplate[]>([]);
    const [schedules, setSchedules] = useState<InspectionSchedule[]>([]);
    // Räknas upp vid varje omladdning så flikarna hämtar sina listor igen.
    const [refreshKey, setRefreshKey] = useState(0);

    // Kontroll som är öppen för att fyllas i eller visas. activeSeq räknas upp
    // varje gång en kontroll öppnas så att panelens fält nollställs.
    const [active, setActive] = useState<InspectionDetail | null>(null);
    // Öppen rond: alla platser i en lista, ovanför den enskilda kontrollen.
    const [activeRound, setActiveRound] = useState<number | null>(null);
    // Planerad kontroll från årshjulet: mallen visas att läsa.
    const [planned, setPlanned] = useState<PlannedInfo | null>(null);
    const [activeSeq, setActiveSeq] = useState(0);

    // Det som delas av flera flikar: räknare, mallar och scheman.
    const loadShared = useCallback(async () => {
        try {
            const [s, t, sc] = await Promise.all([fetchInspectionSummary(), fetchInspectionTemplates(), fetchInspectionSchedules()]);
            setSummary(s); setTemplates(t); setSchedules(sc);
            setError(null);
        } catch (e: any) { setError(e.message); }
    }, []);

    useEffect(() => { loadShared(); }, [loadShared]);

    // Efter att något sparats: ladda om det delade och be flikarna hämta sina listor igen.
    const reload = useCallback(async () => {
        await loadShared();
        setRefreshKey(k => k + 1);
    }, [loadShared]);

    const openInspection = useCallback(async (id: number) => {
        try {
            const d = await fetchInspection(id);
            setActive(d);
            setActiveSeq(n => n + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e: any) { setError(e.message); }
    }, []);

    const tabs: { key: Tab; label: string; badge?: number }[] = [
        { key: 'todo', label: 'Att göra', badge: (summary?.pending ?? 0) + (summary?.overdue ?? 0) + (summary?.actions ?? 0) },
        { key: 'year', label: 'Årshjul' },
        { key: 'history', label: 'Historik' },
        { key: 'schedules', label: 'Scheman' },
        { key: 'templates', label: 'Mallar' },
    ];

    // Grupper (från mallarna) som filtret ovanför flikarna visar.
    const groups = useMemo(() => Array.from(new Set(templates.map(t => t.category))).sort((a, b) => a.localeCompare(b, 'sv')), [templates]);

    return (
        <div>
            <PageHeader icon="✅" title="Egenkontroll"
                description="Återkommande kontroller, till exempel brandskydd och underhåll. Gör dem här eller via länken i påminnelsen."
                actions={summary && (
                    <div style={{ display: 'flex', gap: 14, fontSize: 13, color: '#555', flexWrap: 'wrap' }}>
                        <span>Att göra: <b>{summary.pending}</b></span>
                        <span style={{ color: summary.overdue ? '#dc3545' : undefined }}>Försenade: <b>{summary.overdue}</b></span>
                        <span style={{ color: summary.actions_overdue ? '#dc3545' : undefined }}>Åtgärder: <b>{summary.actions}</b>{summary.actions_overdue ? ` (${summary.actions_overdue} försenade)` : ''}</span>
                        <span>Avvikelser senaste 30 dagarna: <b>{summary.deviations_30d}</b></span>
                        {summary.next_due && <span>Nästa: <b>{fmtDate(summary.next_due)}</b></span>}
                    </div>
                )}
            />

            {error && (
                <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: 12, borderRadius: 4, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{error}</span>
                    <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: '#721c24' }}>✕</button>
                </div>
            )}

            {/* ── Planerad kontroll (bara att läsa) ──────────────────────── */}
            {planned && <PlannedPanel planned={planned} templates={templates} onClose={() => setPlanned(null)} />}

            {/* ── Öppen rond ─────────────────────────────────────────────── */}
            {activeRound != null && (
                <RoundPanel roundId={activeRound} refreshKey={refreshKey} onOpen={openInspection} onClose={() => setActiveRound(null)} onError={setError} />
            )}

            {/* ── Öppen kontroll ─────────────────────────────────────────── */}
            {active && (
                <ActiveInspection key={activeSeq} inspection={active} onChange={setActive} onClose={() => setActive(null)} reload={reload} />
            )}

            {/* ── Grupper ────────────────────────────────────────────────── */}
            {groups.length > 1 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: '#666' }}>Grupp:</span>
                    {['', ...groups].map(g => (
                        <button key={g || '__all'} onClick={() => setGroup(g)} style={{
                            padding: '5px 12px', borderRadius: 16, cursor: 'pointer', fontSize: 13,
                            border: `1px solid ${group === g ? '#0f3460' : '#ccc'}`,
                            background: group === g ? '#0f3460' : 'white', color: group === g ? 'white' : '#333',
                        }}>{g || 'Alla'}</button>
                    ))}
                </div>
            )}

            {/* ── Flikar ─────────────────────────────────────────────────── */}
            <div className="surveillance-tabs" style={{ gap: 4 }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={ui.tab(tab === t.key)}>
                        {t.label}{t.badge ? <span style={{ marginLeft: 6, background: tab === t.key ? 'white' : '#dc3545', color: tab === t.key ? '#0f3460' : 'white', borderRadius: 10, padding: '1px 7px', fontSize: 12 }}>{t.badge}</span> : null}
                    </button>
                ))}
            </div>

            {tab === 'todo' && (
                <TodoTab group={group} templates={templates} schedules={schedules} refreshKey={refreshKey}
                    onOpen={openInspection} reload={reload} onError={setError} />
            )}
            {tab === 'year' && (
                <YearTab group={group} refreshKey={refreshKey} onOpen={openInspection} onError={setError}
                    onOpenRound={id => { setActiveRound(id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    onOpenPlanned={p => { setPlanned(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            )}
            {tab === 'history' && (
                <HistoryTab group={group} refreshKey={refreshKey} onOpen={openInspection} onError={setError} />
            )}
            {tab === 'schedules' && (
                <SchedulesTab group={group} templates={templates} schedules={schedules} canEdit={canEdit} reload={reload} onError={setError} />
            )}
            {tab === 'templates' && (
                <TemplatesTab group={group} templates={templates} groups={groups} canEdit={canEdit} reload={reload} onError={setError} />
            )}
        </div>
    );
};

export default Inspections;
